import { ImageProps } from 'components/commercetools-ui/atoms/image';
import { AddressFormDataSource } from 'components/commercetools-ui/molecules/address-form/types/types';
import { Account } from 'types/entity/account';
import { Order } from 'types/entity/order';
import { UpdateAccount } from 'frontastic/hooks/useAccount/types';

export interface AccountTab {
  name: string;
  pageTitle: string;
  pageTitleCss?: {
    fontSize?: string;
    color?: string;
    fontWeight?: number;
  };

  sidebarLabel: string;
  href: string;
  isActive: boolean;
  icon: React.ReactNode;
  showSidebarSection: boolean;
  emptyPageSubTitle?: string | React.ReactNode;
  pageDescription?: any;
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface OrdersStatus {
  pageHeading: string;
  pageHeadingCss: string;
  sidebarLabel: string;
  breadcrumbL2: string;
}

export interface AddressBook {
  pageHeading: string;
  pageHeadingCss: string;
  sidebarLabel: string;
  defaultAddressLabel: string;
  addAddressCta: string;
  addAddressCtaIcon: ImageProps;
  editAddressCta: string;
  deleteAddressCta: string;
  addAddressLabel: string;
  editAddressLabel: string;
  makeDefaultAddressCta: string;
  deleteAddressModalHeading: string;
  deleteAddressModalMessage: string;
  deleteAddressModalDeleteCta: string;
  deleteAddressModalCancelCta: string;
  editAddressIcon: ImageProps;
  deleteAddressIcon: ImageProps;
  breadcrumbL2: string;
  cancelCta: string;
  saveCta: string;
  updateCta: string;
  addressAddedSuccessfully: string;
  addressUpdatedSuccessfully: string;
  addressSaveFailed: string;
  addressUpdateFailed: string;
  addressDeletedSuccessfully: string;
  addressDeleteFailed: string;
  noAddressesSaved: string;
  addressNotExist: string;
  addressCannotBeDeleted: string;
}

export interface PaymentMethodsProps {
  pageHeading: string;
  pageHeadingCss: string;
  sidebarLabel: string;
  breadcrumbL2: string;
  noPaymentMethodsSaved: string;
  addCreditDebitCta: string;
  text: string;
  local: string;
  sectionHeadingLabel?: string;
  addCreditCardLabel?: string;
  removeCardLabel: string;
  setDefaultLabel: string;
  defaultCardLabel: string;
  cancelBtnCTA?: string;
  deleteModalDesc?: string;
  modalTitalLabel?: string;
  cardAddedSuccess: string;
  failedMessage: string;
  expLabel: string;
  cardRemovedSuccess: string;
  cardExpired: string;
  successDefaultMessage: string;
  isDefault: boolean;
  cardIsPresentLabel?: string;
  stripeError?: string;
  addCardError?: string;
  removeCardError?: string;
  setAsDefaultError?: string;
}

export interface AccountDashboard {
  pageHeading: string;
  pageHeadingCss: string;
  pageSubHeading: string;
  sidebarLabel: string;
  breadcrumbL2: string;
  addressBookHeading: string;
  addressBookDescription: string;
  addressBookIcon: ImageProps | null;
  orderStatusHeading: string;
  orderStatusDescription: string;
  orderStatusIcon: ImageProps | null;
  paymentMethodsHeading: string;
  paymentMethodsDescription: string;
  paymentMethodsIcon: ImageProps | null;
  favoritesHeading: string;
  favoritesDescription: string;
  favoritesIcon: ImageProps | null;
  myProjectsHeading: string;
  myProjectsDescription: string;
  myProjectsIcon: ImageProps | null;
  customerService: string;
  customerServicePhoneNumber: string;
  myPhotosHeading: string;
  myPhotosDescription: string;
  myPhotosIcon: ImageProps | null;
}

export interface MyPhotosText {
  pageHeading: string;
  pageHeadingCss: string;
  pageSubHeading: string;
  sidebarLabel: string;
  breadcrumbL2: string;
  emptyPageTitle?: string;
  emptyPageDescription?: string;
  shopNowCtaLabel?: string;
  previewDrawerAriaLabel?: string;
  dismissInfoBannerAriaLabel?: string;
  previewPhotoAriaLabel?: string;
  removePhotoAriaLabel?: string;
  previewPhotoError?: string;
  infoBannerMessage?: string;
  removeAllCtaLabel?: string;
  removePhotoSuccessMessage?: string;
  removePhotoErrorMessage?: string;
  removeCtaLabel?: string;
  cancelCtaLabel?: string;
  goBackCtaLabel?: string;
  previewHeading?: string;
  previousPhotoAriaLabel?: string;
  nextPhotoAriaLabel?: string;
  removeModalHeading?: string;
  removeModalDescription?: string;
}

export interface AccountTabsText {
  orderStatus: [
    {
      pageHeading: string;
      headerText?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      pageTitleCss?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      sidebarLabel: string;
      breadcrumbL2: string;
    },
  ];
  addressBook: [
    {
      pageHeading: string;
      headerText?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      pageTitleCss?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      sidebarLabel: string;
      breadcrumbL2: string;
      noAddressesSaved: string;
    },
  ];
  paymentMethods: [
    {
      pageHeading: string;
      headerText?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      pageTitleCss?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      sidebarLabel: string;
      breadcrumbL2: string;
      noPaymentMethodsSaved: string;
    },
  ];
  accountDashboard: [
    {
      pageHeading: string;
      headerText?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      pageTitleCss?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      pageSubHeading: string;
      sidebarLabel: string;
      breadcrumbL2: string;
    },
  ];
  myPhotos: [
    {
      pageHeading: string;
      pageTitleCss?: {
        fontSize?: string;
        color?: string;
        fontWeight?: number;
      };
      sidebarLabel: string;
      breadcrumbL2: string;
    },
  ];
}

export interface AccountTabsDataSource {
  dataSource: {
    config: {
      textConfiguration: [AccountTabsText];
    };
  };
}
export type ReasonCode = {
  reasonCode: string;
  codeDescription: string;
};

export type StatusMap = {
  submitted: string;
  processing: string;
  fulfillment: string;
  shipped: string;
  cancelled: string;
  hold: string;
  pickedUp: string;
  destroyed: string;
  refunded: string;
  remake: string;
  delivered: string;
};
export type OrderStatusConfig = StatusMap[];
export type AccountInfo = {
  enableSavedAddresses?: boolean;
  accountErrorMessage: string;
  mainHeading?: string;
  subHeading?: string;
  tableHeadingGroup?: any[];
  tbloadmore?: string;
  clearAll?: string;
  activeFilters?: string;
  tdFulfilmentError?: string;
  dateFilters?: any[];
  deliveryMethodFilters?: any[];
  statusFilters?: any[];
  breadcrumbL0: string;
  breadcrumbL1: string;
  breadcrumbL2: string;
  emptyStateMessage?: string;
  emptyStateCTA?: string;
  isEurope?: boolean;
  reorderEligible?: string;
  noOrdersFilter?: string;
  accountTabs: AccountTabsDataSource;
  ordersStatus: OrdersStatus[];
  addressBook: AddressBook[];
  accountDashboard: AccountDashboard[];
  myPhotos: MyPhotosText[];
  addressFormText: AddressFormDataSource;
  linkTexts?: any;
  paymentMethods: PaymentMethodsProps[];
  shipToHome?: string;
  pickupAtStore?: string;
  reasonCodes?: ReasonCode[];
  paymentCardImages?: any;
  p2rStatus: OrderStatusConfig;
  m2hStatus: OrderStatusConfig;
};

export type AccountDetailsProps = AccountInfo & {
  hash: string;
  id: string | null;
  orders?: Order[];
  ordersLoading?: boolean;
  account?: Account;
  changePassword?: (oldPassword: string, newPassword: string) => Promise<Account>;
  deleteAccount?: (password: string) => Promise<{ success: boolean }>;
  update?: (payload: UpdateAccount) => Promise<Account>;
};

export enum TabsPath {
  ORDERS_STATUS = 'order-status',
  ADDRESS_BOOK = 'address-book',
  PAYMENT_METHODS = 'payment-methods',
  MY_ACCOUNT = 'my-account',
  MY_PHOTOS = 'my-photos',
}

export enum TabsPathUnderAddressBook {
  ADD_NEW_ADDRESS = 'add-new-address',
  EDIT_ADDRESS = 'edit-address',
}

export enum TabsPathUnderMyAccount {
  ADDRESS_BOOK = 'address-book',
  ORDERS_STATUS = 'order-status',
  PAYMENT_METHODS = 'payment-methods',
  FAVORITES = 'favorites',
  MY_PROJECTS = 'my-project',
  MY_PHOTOS = 'my-photos',
}

export enum TabsPaymentMethods {
  ADD_CREDIT_DEBIT_CARD = 'Add Credit/ Debit Card',
  SET_UP_PAYMENT = 'setup-payment',
}